import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { mapList, mapRetrieve } from '../common/rest.operators';
import { RoleItem, RoleModelSetTransformer } from '../user/role.user.model';

import { UserReport, UserReportDetail } from './reports.model';
import { Report2_2Parameters } from './scatterplot/scatterplot.model';
import { TdBuilderUsageIndividualReportPopulationType } from './td-builder-usage-individual/td-builder-usage-individual.reports.model';
import { TdBuilderUsageMetricReportPopulationType } from './td-builder-usage-metric/td-builder-usage-metric.reports.model';
import {
    TdComplianceReportPopulationType,
    TdComplianceUser,
} from './td-compliance/td-compliance.reports.model';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
    TdClientReportConfig,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TdDailyHuddleBoard,
    TdDailyHuddleMetric,
    TdDailyHuddleResult,
    TdDailyHuddleRole,
    TdDailyHuddleUser,
    TdRawAgentDataDates,
} from './td-daily-huddle/td-daily-huddle.reports.model';
import {
    TdOffersReportResultParams,
    TdOffersUser,
} from './td-offers/td-offers.reports.model';
import { TargetParameters } from './td-tbyt-selection/report_tbyt-selection.reports.model';

@Injectable()
export class ReportsService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    retrieveUserInfo(
        startDate: Date,
        endDate: Date,
        rolesId: string,
    ): Observable<UserReport[]> {
        let params = new HttpParams().set('rolesId', rolesId);
        params = params.set('startDate', startDate.toJSON());
        params = params.set('endDate', endDate.toJSON());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GenerateLoginReport`,
                { params },
            )
            .pipe(
                mapList(),
                // tslint:disable-next-line: deprecation
                catchError(() => of([])),
            );
    }

    retrieveUserInfoDetail(
        startDate: Date,
        endDate: Date,
        userId: number,
    ): Observable<UserReportDetail[]> {
        let params = new HttpParams().set('userId', userId.toString());
        params = params.set('startDate', startDate.toJSON());
        params = params.set('endDate', endDate.toJSON());
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GenerateIndividualLoginAttempt`,
                { params },
            )
            .pipe(
                mapList(),
                // tslint:disable-next-line: deprecation
                catchError(() => of([])),
            );
    }

    // DailyHuddleBoardData
    retrieveDailyHuddleBoardData(
        targetId: number,
        roleId: number,
        date: Date,
        metricList: string,
    ): Observable<number> {
        let params = new HttpParams().set('targetId', targetId.toString());
        params = params.set('roleId', roleId.toString());
        params = params.set('date', date.toJSON());
        params = params.set('metricList', metricList);

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/DailyHuddleBoardData`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.log('error api: ', e);
                    // tslint:disable-next-line: deprecation
                    return of(false);
                }),
            );
    }

    retrieveRxRData(
        targetId: number,
        roleId: number,
        date: Date,
        dateRange: string,
        metricList: string,
    ): Observable<number> {
        let params = new HttpParams().set('targetId', targetId.toString());
        params = params.set('roleId', roleId.toString());
        params = params.set('date', date.toJSON());
        params = params.set('dateRange', dateRange);
        params = params.set('metricList', metricList);

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GenerateRxRData`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.log('error api: ', e);
                    // tslint:disable-next-line: deprecation
                    return of(0);
                }),
            );
    }

    retrieveReport2_2Data(
        reportTbtParameters: Report2_2Parameters,
    ): Observable<number> {
        let params = new HttpParams().set(
            'userId',
            reportTbtParameters.userId.toString(),
        );
        params = params.set('roleId', reportTbtParameters.roleId.toString());
        params = params.set(
            'behaviourMetricID',
            reportTbtParameters.behaviourMetricID.toString(),
        );
        params = params.set(
            'outcomeMetricID',
            reportTbtParameters.outcomeMetricID.toString(),
        );
        params = params.set('date', reportTbtParameters.date);

        // let params = new HttpParams().set('userId', '3320');
        // params = params.set('roleId', '4');
        // params = params.set('behaviourMetricID', '116');
        // params = params.set('outcomeMetricID', '117');
        // params = params.set('date', '2018-09-15');

        return (
            this._http
                // .get<DailyHuddleBoard>("http://www.mocky.io/v2/5d6421d83200005d00ba1f84?mocky-delay=1s")
                .get(
                    `${this._appConfig.API_ENDPOINTS.REPORT}/report/Report2_2Data`,
                    { params },
                )
                .pipe(
                    mapRetrieve(),
                    // map(r => r as any),
                    catchError((e) => {
                        console.log('error api: ', e);
                        // tslint:disable-next-line: deprecation
                        return of();
                    }),
                )
        );
    }

    retrieveReportOffersData(
        reportOffersParameters: TdOffersReportResultParams,
    ): Observable<number> {
        let params = new HttpParams().set(
            'populationTypeID',
            reportOffersParameters.populationType.toString(),
        );
        params = params.set(
            'populationID',
            reportOffersParameters.populationID,
        );
        params = params.set(
            'startDate',
            reportOffersParameters.startDate.toString(),
        );
        params = params.set(
            'endDate',
            reportOffersParameters.endDate.toString(),
        );

        return (
            this._http
                // .get<DailyHuddleBoard>("http://www.mocky.io/v2/5d6421d83200005d00ba1f84?mocky-delay=1s")
                .get(
                    `${this._appConfig.API_ENDPOINTS.REPORT}/report/TD_GenerateOffers`,
                    { params },
                )
                .pipe(
                    mapRetrieve(),
                    // map(r => r as any),
                    catchError((e) => {
                        console.log('error api: ', e);
                        // tslint:disable-next-line: deprecation
                        return of(0);
                    }),
                )
        );
    }

    retrieveRolesHierarchy(userId: number, date: Date): Observable<RoleItem[]> {
        let params = new HttpParams().set('userId', userId.toString());
        params = params.set('date', date.toJSON());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/role/GetRolesHierarchy`,
                { params },
            )
            .pipe(
                mapList(new RoleModelSetTransformer()),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of([]);
                }),
            );
    }

    // GetDailyHuddleReportResult
    retrieveGenericReportResult(
        reportId: number,
    ): Observable<TdDailyHuddleResult> {
        const endpoint = `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetGenericReportResult`;
        const params = new HttpParams().set('reportId', reportId.toString());
        return this._http.get(endpoint, { params }).pipe(
            mapRetrieve(),
            catchError((e) => {
                console.log('error api: ', e);
                // tslint:disable-next-line: deprecation
                return of({});
            }),
        );
    }

    retrieveRolesNameById(roleIds: string): Observable<TdDailyHuddleRole[]> {
        const params = new HttpParams().set('roleIds', roleIds);
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetRolesNameById`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    getSubordinatedRoles(roleId: number): Observable<any> {
        const params = new HttpParams().set('managerRoleId', roleId.toString());
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UserProfile/GetSubordinatedRoles`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    retrieveUsersNameById(userIds: string): Observable<TdDailyHuddleUser[]> {
        const params = new HttpParams().set('userIds', userIds);
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetUsersNameById`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    public retrieveRawAgentDataDates(): Observable<TdRawAgentDataDates> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetRawAgentDataDates`,
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    public retrieveCallComplianceDates(): Observable<TdRawAgentDataDates> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetCallComplianceDates`,
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    public retrieveMetricTarget(
        parameters: TargetParameters,
    ): Observable<string> {
        let params = new HttpParams().set('metricId', parameters.metricId);
        params = params.set('date', parameters.date);
        params = params.set('roleId', parameters.roleId);
        params = params.set('userId', parameters.userId);
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetMetricTarget`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    public retrieveConfigReportSelectedJson(
        reportId: number,
    ): Observable<TdClientReportConfig> {
        const params = new HttpParams().set('reportId', reportId.toString());
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetConfigReportSelectedJson`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                map((r) => JSON.parse(r)),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of('');
                }),
            );
    }

    public retrieveMetricNames(
        metricList: string,
    ): Observable<TdDailyHuddleMetric[]> {
        const params = new HttpParams().set('metricList', metricList);
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetMetricNames`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                map((metrics: any[]) =>
                    metrics.map((m) => ({
                        id: m.metricID,
                        name: m.metricName,
                    })),
                ),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of([]);
                }),
            );
    }

    retrieveDefaultHierarchyUser(
        id: number = null,
    ): Observable<TdComplianceUser | TdOffersUser> {
        const params =
            id != null ? new HttpParams().set('userId', id.toString()) : null;
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetDefaultHierarchyUser`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    generateTdCompliance(
        populationID: number,
        populationType: TdComplianceReportPopulationType,
        date: Date,
    ): Observable<number> {
        let params = new HttpParams().set(
            'populationID',
            populationID.toString(),
        );
        params = params.set('populationType', populationType.toString());
        params = params.set('date', date.toJSON());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/TD_GenerateCompliance`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.log('error api: ', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    generateTdBuilderUsageMetric(
        populationID: number,
        populationType: TdBuilderUsageMetricReportPopulationType,
        startDate: Date,
        endDate: Date,
    ): Observable<number> {
        let params = new HttpParams().set(
            'populationID',
            populationID.toString(),
        );
        params = params.set('populationType', populationType.toString());
        params = params.set('startDate', startDate.toJSON());
        params = params.set('endDate', endDate.toJSON());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/TD_GenerateBuilderUsageMetric`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.log('error api: ', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    generateTdBuilderUsageIndividual(
        populationID: number,
        populationType: TdBuilderUsageIndividualReportPopulationType,
        startDate: Date,
        endDate: Date,
    ): Observable<number> {
        let params = new HttpParams().set(
            'populationID',
            populationID.toString(),
        );
        params = params.set('populationType', populationType.toString());
        params = params.set('startDate', startDate.toJSON());
        params = params.set('endDate', endDate.toJSON());

        console.log('Individual Parameters');
        console.log(params);

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/TD_GenerateBuilderUsageIndividual`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.log('error api: ', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }
}
